/**
 * <venoa-gallery-scents> – Einzelmotiv je Duft in der oberen Produktgalerie
 * (snippets/venoa-gallery-scents.liquid).
 *
 * - Seitenaufruf (auch mit ?variant=…): Gruppenbild, keine Ebene.
 * - Bewusste Duftwahl (Klick/Tipp in der Duftleiste; bei Pistachio Vail auf
 *   dessen Schaltfläche): Das Einzelmotiv blendet sich über das Gruppenbild;
 *   zwei Vorschaubilder im Bild führen zurück zum Gruppenbild und wieder hin.
 * - Kaufart „Bundle“: wieder das Gruppenbild, Vorschaubilder ausgeblendet.
 *
 * Nur Darstellung: Die Ebene liegt über dem sichtbaren ersten Galeriebild,
 * Klicks auf das Bild gehen weiter an die Galerie. Produktmedien, Varianten,
 * Preise und Warenkorb bleiben unberührt. Programmatische Wechsel (Deep-Links,
 * Skripte) zählen nicht als bewusste Auswahl.
 *
 * Bildwechsel: Das bisherige Bild bleibt voll sichtbar, bis das neue geladen
 * und dekodiert ist. Vom Gruppenbild aus legt sich das Einzelmotiv in einem
 * kurzen Einblenden darüber. Von Duft zu Duft wischt das Bild immer von
 * rechts nach links: Das neue Motiv steht rechts neben dem alten, beide
 * fahren gemeinsam als ein Streifen ([data-gallery-track]) nach links – keine
 * Überlappung, keine Lücke, nie Gruppenbild oder Hintergrund dazwischen.
 * Zurück zum Gruppenbild
 * blendet die Ebene als Ganzes aus. Neue Auswahl während eines Wechsels: der
 * laufende Wechsel springt an sein Ende, die letzte Auswahl gewinnt. Dieselbe
 * Auswahl erneut: keine Animation.
 */
if (!customElements.get('venoa-gallery-scents')) {
  const PICKER = '.product-information variant-picker';
  const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');
  const FADE_IN = { duration: 260, easing: 'cubic-bezier(0.2, 0, 0, 1)' };
  const FADE_OUT = { duration: 240, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' };
  const SWIPE = { duration: 560, easing: 'cubic-bezier(0.4, 0, 0.1, 1)' };
  const DECODE_WAIT = 4000;
  const state = { scent: null, show: 'group', bundle: false };

  customElements.define(
    'venoa-gallery-scents',
    class VenoaGalleryScents extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;
        this.layer = this.querySelector('[data-gallery-layer]');
        this.track = this.querySelector('[data-gallery-track]');
        this.switcher = this.querySelector('[data-gallery-switch]');
        this.figures = [...this.querySelectorAll('[data-gallery-scent]')];
        this.scentThumb = this.querySelector('[data-gallery-scent-thumb]');
        this.scentLabel = this.querySelector('[data-gallery-scent-label]');
        if (!this.layer || !this.figures.length) return;

        this.controller = new AbortController();
        const { signal } = this.controller;
        document.addEventListener('click', this.onClick, { capture: true, signal });
        document.addEventListener('change', this.onChange, { signal });
        document.addEventListener('shopify:product:select', this.onProductSelect, { signal });
        window.matchMedia('(min-width: 750px)').addEventListener('change', () => this.place(), { signal });
        // Vorschaubilder liegen im Galeriebild, das selbst den Zoom öffnet (Dwell
        // lauscht auf dem Dokument). Klicks darauf werden deshalb schon am
        // Fenster abgefangen und erreichen Zoom und Wischen der Galerie nicht.
        window.addEventListener('click', this.onSwitch, { capture: true, signal });
        window.addEventListener('pointerdown', this.shield, { capture: true, signal });

        this.place();
        this.render(false);

        // Einzelmotive und Vorschaubilder vorab laden und dekodieren, sobald die
        // Seite ruht – dann steht beim ersten Antippen alles bereit.
        const preload = () =>
          this.figures.forEach((figure) => {
            if (figure.dataset.thumb) new Image().src = figure.dataset.thumb;
            this.ready(figure);
          });
        if ('requestIdleCallback' in window) window.requestIdleCallback(preload, { timeout: 2500 });
        else window.setTimeout(preload, 1500);
      }

      disconnectedCallback() {
        // Wird nur beim echten Entfernen aufgeräumt (place() bewegt die Kinder, nicht das Element).
        if (this.isConnected) return;
        this.controller?.abort();
        this.bound = false;
      }

      /** Ebene und Vorschaubilder in das gerade sichtbare erste Galeriebild legen. */
      place() {
        const containers = [...document.querySelectorAll('.product-information media-gallery .product-media-container')];
        const host = containers.find((el) => el.offsetWidth > 0 && el.offsetHeight > 0);
        if (!host) return;
        host.classList.add('venoa-gallery-host');
        if (this.layer.parentElement !== host) host.append(this.layer);
        if (this.switcher && this.switcher.parentElement !== host) host.append(this.switcher);
      }

      /** Ist die Ebene noch im Dokument (Galerie ggf. neu aufgebaut)? Sonst neu einhängen. */
      ensurePlaced() {
        if (!this.layer.isConnected || !this.layer.parentElement?.offsetWidth) this.place();
      }

      figureFor(value) {
        const wanted = String(value || '').trim().toLowerCase();
        if (!wanted) return null;
        return (
          this.figures.find((figure) => figure.dataset.galleryScent.trim().toLowerCase() === wanted) ||
          this.figures.find((figure) => wanted.startsWith(figure.dataset.galleryScent.trim().toLowerCase().split(' ')[0]))
        );
      }

      /* ---------- Bewusste Auswahl ---------- */

      onClick = (event) => {
        if (!event.isTrusted || !(event.target instanceof Element)) return;
        // Pistachio Vail (eigene Schaltfläche in der Duftleiste, nur im Vier-Düfte-Theme)
        if (event.target.closest(`${PICKER} [data-venoa-pv-option]`)) {
          this.choose('pistachio');
          return;
        }
        // Tipp auf einen Duft – auch auf den bereits gewählten
        const input = event.target.closest(`${PICKER} input[type="radio"]`);
        if (input) window.requestAnimationFrame(() => this.choose(input.value));
      };

      onChange = (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement)) return;
        if (input.matches('[data-buymode-input]')) {
          state.bundle = input.value !== 'single';
          if (state.bundle) state.show = 'group';
          else if (state.scent) {
            // Zurück zum Einzelkauf: Das Vorschaubild bietet den jetzt gewählten
            // Duft an (er kann im Bundle-Modus geändert worden sein).
            const checked = document.querySelector(`${PICKER} input[type="radio"]:checked`);
            const figure = this.figureFor(checked?.value);
            if (figure) state.scent = figure.dataset.galleryScent;
          }
          this.render(true);
        }
      };

      onProductSelect = (event) => {
        const promise = /** @type {any} */ (event).promise;
        if (!promise || typeof promise.then !== 'function') return;
        promise
          .then(() => {
            this.ensurePlaced();
            this.render(false);
          })
          .catch(() => {});
      };

      choose(value) {
        const figure = this.figureFor(value);
        if (!figure || state.bundle) return;
        state.scent = figure.dataset.galleryScent;
        state.show = 'scent';
        this.ensurePlaced();
        this.render(true);
      }

      shield = (event) => {
        if (event.target instanceof Element && this.switcher?.contains(event.target)) event.stopPropagation();
      };

      onSwitch = (event) => {
        if (!(event.target instanceof Element) || !this.switcher?.contains(event.target)) return;
        event.stopPropagation();
        const button = event.target.closest('[data-gallery-show]');
        if (!button) return;
        event.preventDefault();
        state.show = button.dataset.galleryShow === 'scent' && state.scent ? 'scent' : 'group';
        this.render(true);
      };

      /* ---------- Darstellung ---------- */

      /** Einzelmotiv geladen und dekodiert (höchstens DECODE_WAIT warten). */
      ready(figure) {
        const img = figure?.querySelector('img');
        if (!img) return Promise.resolve();
        if (!figure.decoded) {
          if (img.loading === 'lazy') img.loading = 'eager';
          figure.decoded = (img.decode ? img.decode() : Promise.resolve()).catch(
            () =>
              new Promise((resolve) => {
                if (img.complete) resolve();
                else img.addEventListener('load', resolve, { once: true });
              })
          );
        }
        return Promise.race([figure.decoded, new Promise((r) => setTimeout(r, DECODE_WAIT))]);
      }

      /** Laufende Ein-/Ausblendungen sofort in ihren Endzustand bringen. */
      finishFades() {
        (this.fades || []).forEach((animation) => {
          try {
            animation.finish();
          } catch {
            /* bereits beendet */
          }
        });
        this.fades = [];
      }

      /** Nur dieses Motiv (oder keines = Gruppenbild) sichtbar, ohne Animation. */
      settle(target) {
        this.figures.forEach((figure) => {
          figure.classList.toggle('is-shown', figure === target);
          figure.style.removeProperty('z-index');
          figure.style.removeProperty('transform');
        });
        // Streifen zurück auf Anfang – im selben Schritt, daher kein Zucken
        this.track?.getAnimations().forEach((animation) => animation.cancel());
        this.top = target;
        this.fades = (this.fades || []).filter((animation) => animation.playState === 'running');
      }

      async render(animate) {
        const current = state.show === 'scent' && !state.bundle ? this.figureFor(state.scent) : null;
        const showSwitch = Boolean(state.scent) && !state.bundle;
        const chosen = showSwitch ? this.figureFor(state.scent) : null;
        this.updateSwitch(showSwitch, chosen, current);

        // Dasselbe Ziel wie zuletzt angefordert: nichts neu starten (erneutes
        // Antippen, Dwells Bestätigung nach dem Variantenwechsel).
        if (this.requested !== undefined && current === this.requested) return;
        this.requested = current;
        const token = (this.token = (this.token || 0) + 1);

        const instant = !animate || REDUCE.matches || !this.layer.animate;
        if (current && !instant) {
          // Bisheriges Bild bleibt stehen, bis das neue bereit ist.
          await this.ready(current);
          if (token !== this.token) return;
        }

        if (instant) {
          this.finishFades();
          this.layer.style.removeProperty('opacity');
          this.settle(current);
          return;
        }

        if (current) {
          // Lief gerade das Ausblenden zum Gruppenbild: Gruppenbild gilt als
          // erreicht (kein altes Motiv taucht wieder auf), das neue kommt darüber.
          const leaving = (this.fades || []).filter((a) => a.effect?.target === this.layer);
          if (leaving.length) {
            leaving.forEach((a) => a.cancel());
            this.finishFades();
            this.settle(null);
          } else if ((this.fades || []).some((a) => a.playState === 'running')) {
            // Lief noch ein Wechsel zu einem Motiv: sofort abschließen, von dort weiter.
            this.finishFades();
            this.settle(this.goal ?? null);
          }
          this.goal = current;

          // Von Duft zu Duft: neues Motiv rechts daneben, Streifen wischt nach links.
          const from = this.top;
          if (this.track && from && from !== current) {
            current.style.transform = 'translateX(100%)';
            current.classList.add('is-shown');
            const swipe = this.track.animate(
              [{ transform: 'translateX(0)' }, { transform: 'translateX(-100%)' }],
              { ...SWIPE, fill: 'forwards' }
            );
            this.fades.push(swipe);
            try {
              await swipe.finished;
            } catch {
              return; // abgebrochen – ein neuerer Wechsel räumt auf
            }
            if (token !== this.token) return;
            this.fades = this.fades.filter((a) => a !== swipe);
            this.settle(current);
            return;
          }

          this.z = (this.z || 1) + 1;
          current.style.zIndex = String(this.z);
          current.classList.add('is-shown');
          const fade = current.animate([{ opacity: 0 }, { opacity: 1 }], { ...FADE_IN, fill: 'backwards' });
          this.fades.push(fade);
          try {
            await fade.finished;
          } catch {
            return; // abgebrochen – ein neuerer Wechsel räumt auf
          }
          if (token !== this.token) return;
          this.fades = this.fades.filter((a) => a !== fade);
          this.settle(current);
          return;
        }

        // Zurück zum Gruppenbild: Ebene als Ganzes ausblenden (kein Doppelbild).
        const visible = this.figures.filter((figure) => figure.classList.contains('is-shown'));
        if (!visible.length) return this.settle(null);
        this.goal = null;
        const fade = this.layer.animate([{ opacity: 1 }, { opacity: 0 }], { ...FADE_OUT, fill: 'forwards' });
        this.fades = [...(this.fades || []), fade];
        try {
          await fade.finished;
        } catch {
          return;
        }
        if (token !== this.token) return;
        this.finishFades();
        this.settle(null);
        fade.cancel();
      }

      /** Vorschaubilder: sichtbar, sobald ein Duft gewählt wurde (nicht im Bundle). */
      updateSwitch(showSwitch, chosen, current) {
        if (this.switcher) {
          this.switcher.hidden = !showSwitch;
          this.switcher.querySelectorAll('[data-gallery-show]').forEach((button) => {
            const pressed = (button.dataset.galleryShow === 'scent') === Boolean(current);
            button.setAttribute('aria-pressed', String(pressed));
          });
        }
        if (!showSwitch) return;
        const thumb = chosen?.dataset.thumb;
        if (this.scentThumb) {
          if (thumb && this.scentThumb.getAttribute('src') !== thumb) this.scentThumb.src = thumb;
          if (!thumb) this.scentThumb.removeAttribute('src');
          this.scentThumb.closest('button')?.classList.toggle('is-swatch', !thumb);
        }
        if (this.scentLabel) this.scentLabel.textContent = chosen?.querySelector('img')?.alt || 'Einzelmotiv';
      }
    }
  );
}
